import { Outlet } from "react-router";
import classNames from "classnames";
import { Flex, Division } from "@ir/ui";
import Sidebar from "@/presentation/ui/components/sidebar/sidebar";
import { useSidebar, useSocketListener, useEventBusEmitter } from "@/hooks";
import { WebSocketAction, WebSocketResponse } from "@/core/interface/WebSocketResponse";
import { JsonSerializer } from "@/infrastructure/serializers/JsonSerializer";
import { DeductionIncompleteProvider } from "@/presentation/deduction/components/DeductionDrawer/DeductionIncomplete/context";
import DeductionDrawerMain from "@/presentation/deduction/components/DeductionDrawer/DeductionDrawerMain";
import { BackupRequestEvents, BackupRequestStatusUpdatePayload } from "@/core/events/backupRequestEventsMap";
import { DisputeEvents, DisputeStatusUpdatePayload } from "@/core/events/disputeEventsMap";
import { useConversationNotifications } from "./hooks/useConversationNotifications";
import { createLogger } from "@/infrastructure/services/logger";
import PromotionDrawer from "@/presentation/pages/trade-promotions/components/PromotionDrawer";
import { useApproval } from "@/presentation/pages/approvals/hooks";
import { HasAllPermissions } from "@/presentation/workspace";
import { WorkspacePermissions } from "@/core/permissions";
import { ApprovalModal } from "@/presentation/pages/approvals/components";

const jsonSerializer = new JsonSerializer();

const WorkspaceRootLayout = () => {
  const logger = createLogger("WorkspaceRootLayout");
  const { isCollapsedSidebar } = useSidebar();
  const { emit } = useEventBusEmitter();
  const { contextHolder } = useConversationNotifications();

  const {
    selectedApprovals,
    selectedApproval,
  } = useApproval();

  // Handle backup request status updates and dispute status updates
  useSocketListener({
    onProcessed: (wsResponse: string) => {
      const response = jsonSerializer.deserialize<WebSocketResponse<any>>(wsResponse);

      switch (response.action) {
        // Handle backup request status updates
        case WebSocketAction.BACKUP_REQUEST_STATUS_UPDATE: {
          const backupRequestStatusUpdatePayload = new BackupRequestStatusUpdatePayload(response.data);
          emit(BackupRequestEvents.BACKUP_REQUEST_STATUS_UPDATE, backupRequestStatusUpdatePayload, {
            onReject: () => {
              logger.error("BACKUP_REQUEST_STATUS_UPDATE_REJECTED", backupRequestStatusUpdatePayload);
            },
            onNoSubscribers: () => {
              logger.error("BACKUP_REQUEST_STATUS_UPDATE_NO_SUBSCRIBERS", backupRequestStatusUpdatePayload);
            },
          });
          break;
        }

        // Handle dispute status updates
        case WebSocketAction.DISPUTE_STATUS_UPDATE: {
          const disputeStatusUpdatePayload = new DisputeStatusUpdatePayload(response.data);
          emit(DisputeEvents.DISPUTE_STATUS_UPDATE, disputeStatusUpdatePayload, {
            onReject: () => {
              logger.error("DISPUTE_STATUS_UPDATE_REJECTED", disputeStatusUpdatePayload);
            },
            onNoSubscribers: () => {
              logger.error("DISPUTE_STATUS_UPDATE_NO_SUBSCRIBERS", disputeStatusUpdatePayload);
            },
          });
          break;
        }
        default:
          logger.error(`Unknown event: ${response.action}`);
          break;
      }
    },
    enabled: true,
  });

  const mainContentClassNames = classNames("main-content", {
    collapse: isCollapsedSidebar,
    "main-content-collapsed": isCollapsedSidebar,
    "main-content-expanded": !isCollapsedSidebar,
  });

  if (isCollapsedSidebar == null) {
    return null;
  }

  return (
    <Flex align="center">
      <Sidebar />
      <Division className={mainContentClassNames}>
        <Outlet />
        <DeductionIncompleteProvider>
          <DeductionDrawerMain />
        </DeductionIncompleteProvider>
        <PromotionDrawer />
        <HasAllPermissions
          requiredPermissions={[
            WorkspacePermissions.DEDUCTIONS_APPROVAL_VIEW,
            WorkspacePermissions.DEDUCTIONS_SEND_APPROVAL,
          ]}
        >
          <ApprovalModal selectedApprovals={selectedApproval ? [selectedApproval] : selectedApprovals} />
        </HasAllPermissions>
        {contextHolder}
      </Division>
    </Flex>
  );
};

export default WorkspaceRootLayout;
